import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiKey } from "../../config/config";

function TrackOrder({ logo }) {
  const { trackingNo } = useParams();
  const [trackingNumber, setTrackingNumber] = useState(trackingNo || "");
  const [activeTrackingNumber, setActiveTrackingNumber] = useState(trackingNo || "");
  const [currentStep, setCurrentStep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const statuses = useMemo(() => [
    {
      id: 0,
      label: "Not Approved",
      description: "Order awaiting approval",
      icon: "⏳",
    },
    {
      id: 1,
      label: "Approved",
      description: "Order has been approved",
      icon: "✓",
    },
    // {
    //   id: 2,
    //   label: "Cooking Pending",
    //   description: "Waiting to start cooking",
    //   icon: "⏱",
    // },
    // {
    //   id: 3,
    //   label: "Cooking Started",
    //   description: "Your order is being prepared",
    //   icon: "👨‍🍳",
    // },
    {
      id: 2,
      label: "Rider Assigned",
      description: "Delivery rider assigned",
      icon: "🏍",
    },
    {
      id: 3,
      label: "Picked",
      description: "Order picked up",
      icon: "📦",
    },
    {
      id: 4,
      label: "On The Way",
      description: "Order is on its way to you",
      icon: "🚗",
    },
    {
      id: 5,
      label: "Delivered",
      description: "Order delivered successfully",
      icon: "🎉",
    },
  ], []);

  const getStepFromStatus = useCallback((statusLabel) => {
    const foundStatus = statuses.find(
      (status) => status.label.toLowerCase() === statusLabel.toLowerCase()
    );

    return foundStatus ? foundStatus.id : 0;
  }, [statuses]);

  const handleSearch = useCallback((isPolling = false) => {
    const targetNumber = isPolling === true ? activeTrackingNumber : trackingNumber;

    if (!targetNumber || !targetNumber.trim()) {
      if (isPolling !== true) setError("Please enter a tracking number");
      return;
    }

    if (isPolling !== true) {
      setLoading(true);
      setError("");
      setActiveTrackingNumber(targetNumber);
    }

    axios
      .get(`${apiKey}/api/rms/customer/trackOrder/${targetNumber}`)
      .then((res) => {
        if (res.data && res.data) {
          const stepId = getStepFromStatus(res.data);
          setCurrentStep(stepId);
          if (isPolling !== true) setError("");
        } else {
          if (isPolling !== true) setError("Invalid order data received");
          setCurrentStep(null);
        }
      })
      .catch((err) => {
        console.log(err);
        if (isPolling !== true) {
          setError(
            err.response?.data?.message ||
            "Order not found. Please check the tracking number."
          );
          setCurrentStep(null);
        }
      })
      .finally(() => {
        if (isPolling !== true) setLoading(false);
      });
  }, [trackingNumber, activeTrackingNumber, getStepFromStatus]);

  useEffect(() => {
    if (trackingNo) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingNo]);

  useEffect(() => {
    let intervalId;
    if (activeTrackingNumber) {
      intervalId = setInterval(() => {
        handleSearch(true);
      }, 10000); // 10 seconds interval
    }
    return () => clearInterval(intervalId);
  }, [activeTrackingNumber, handleSearch]);

  return (
    <main className="w-full min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      <section className="max-w-6xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="relative mb-12">
          <Link to={"/"} className="absolute top-0 right-0 group">
            <div className="w-10 h-10 flex items-center justify-center rounded-full transition-colors duration-200">
              <svg
                width="24"
                height="24"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.7076 18.2925C19.8005 18.3854 19.8742 18.4957 19.9245 18.6171C19.9747 18.7385 20.0006 18.8686 20.0006 19C20.0006 19.1314 19.9747 19.2615 19.9245 19.3829C19.8742 19.5043 19.8005 19.6146 19.7076 19.7075C19.6147 19.8004 19.5044 19.8741 19.383 19.9244C19.2616 19.9747 19.1315 20.0006 19.0001 20.0006C18.8687 20.0006 18.7386 19.9747 18.6172 19.9244C18.4958 19.8741 18.3855 19.8004 18.2926 19.7075L10.0001 11.4138L1.70757 19.7075C1.51993 19.8951 1.26543 20.0006 1.00007 20.0006C0.734704 20.0006 0.480208 19.8951 0.292568 19.7075C0.104927 19.5199 -0.000488276 19.2654 -0.000488281 19C-0.000488286 18.7346 0.104927 18.4801 0.292568 18.2925L8.58632 10L0.292568 1.70751C0.104927 1.51987 -0.000488281 1.26537 -0.000488281 1.00001C-0.000488281 0.734643 0.104927 0.480147 0.292568 0.292507C0.480208 0.104866 0.734704 -0.000549316 1.00007 -0.000549316C1.26543 -0.000549316 1.51993 0.104866 1.70757 0.292507L10.0001 8.58626L18.2926 0.292507C18.4802 0.104866 18.7347 -0.000549322 19.0001 -0.000549316C19.2654 -0.000549311 19.5199 0.104866 19.7076 0.292507C19.8952 0.480147 20.0006 0.734643 20.0006 1.00001C20.0006 1.26537 19.8952 1.51987 19.7076 1.70751L11.4138 10L19.7076 18.2925Z"
                  fill="var(--color-w-btn)"
                />
              </svg>
            </div>
          </Link>

          <div className="text-center mb-8">
            <img
              className="mx-auto h-12 mb-6"
              src={logo}
              alt="Imdady Logo"
            />
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Track Your Order
            </h1>
            <p className="text-gray-600">
              Monitor your order status in real-time
            </p>
          </div>
        </div>

        {/* Search Box */}
        <div className="mb-12 flex justify-center">
          <div className="w-full max-w-md">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Enter your tracking number"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  disabled={loading}
                  className="w-full px-5 py-3 rounded-lg border-2 border-gray-200 focus:border-w-btn focus:outline-none transition-colors duration-200 shadow-sm disabled:bg-gray-100"
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={loading}
                className="px-6 py-3 bg-w-btn hover:bg-w-btn-hover text-white font-semibold rounded-lg transition-all duration-200 hover:shadow-lg flex items-center gap-2 disabled:bg-w-btn-disabled"
              >
                {loading ? (
                  <svg
                    className="w-5 h-5 animate-spin"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                ) : (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M11.76 10.27L17.49 16L16 17.49L10.27 11.76C9.2 12.53 7.91 13 6.5 13C2.91 13 0 10.09 0 6.5C0 2.91 2.91 0 6.5 0C10.09 0 13 2.91 13 6.5C13 7.91 12.53 9.2 11.76 10.27ZM6.5 2C4.01 2 2 4.01 2 6.5C2 8.99 4.01 11 6.5 11C8.99 11 11 8.99 11 6.5C11 4.01 8.99 2 6.5 2Z"
                      fill="white"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Order Status Timeline */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          {/* Error Alert */}
          {error && (
            <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Order Info Card */}
          {trackingNumber && currentStep !== null && (
            <div className="mb-12 p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-100">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Tracking Number</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {trackingNumber}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Current Status</p>
                  <p className="text-xl font-bold text-w-btn">
                    {statuses[currentStep].label}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          {currentStep !== null && (
            <div className="relative">
              {/* Timeline Line - Desktop */}
              <div className="hidden md:block absolute top-8 left-0 right-0 h-1 bg-gray-200">
                <div
                  className={`h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-500`}
                  style={{
                    width:
                      currentStep === 0
                        ? "0%"
                        : `${(currentStep / (statuses.length - 1)) * 100}%`,
                  }}
                />
              </div>

              {/* Timeline Items */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-0 relative z-10">
                {statuses.map((status, index) => (
                  <div key={status.id} className="flex flex-col items-center">
                    {/* Circle */}
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold transition-all duration-300 mb-4 cursor-pointer hover:scale-110 ${index < currentStep
                        ? "bg-green-500 text-white shadow-lg shadow-green-500/50"
                        : index === currentStep
                          ? "bg-w-btn text-white shadow-lg shadow-w-btn/50 ring-4 ring-w-btn-hover"
                          : "bg-gray-300 text-gray-600 shadow-md"
                        }`}
                    >
                      {index < currentStep ? (
                        <svg
                          className="w-8 h-8"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        status.icon
                      )}
                    </div>

                    {/* Label */}
                    <div className="text-center">
                      <h3
                        className={`font-semibold text-sm transition-colors duration-200 ${index <= currentStep
                          ? "text-gray-900"
                          : "text-gray-500"
                          }`}
                      >
                        {status.label}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1 hidden md:block">
                        {status.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Current Status Details */}
          {trackingNumber && currentStep !== null && (
            <div className="mt-12 p-6 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">
                {statuses[currentStep].label}
              </h3>
              <p className="text-gray-700">
                {statuses[currentStep].description}
              </p>
              {currentStep === statuses.length - 1 && (
                <div className="mt-4 flex items-center gap-2 text-green-700 font-semibold">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Your order has been delivered!
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!trackingNumber && (
            <div className="text-center py-8">
              <p className="text-gray-600">
                Enter a tracking number above to view order status
              </p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

export default TrackOrder;